import { createAction, props } from '@ngrx/store';
import type { TaskAttachmentUploadResponse, TaskItem, TaskNotification, TaskProject, TaskProjectUpsertPayload, TaskSprint, TaskSprintUpsertPayload, TaskTimeEntryCreatePayload, TaskTimerStartPayload, TaskTimerStopPayload, TaskUpsertPayload } from './task.models';

export const TaskClearStatus = createAction('[Task] Clear Status');

export const TaskProjectsFetchRequest = createAction('[Task] Projects Fetch Request');
export const TaskProjectsFetchSuccess = createAction('[Task] Projects Fetch Success', props<{ projects: TaskProject[] }>());
export const TaskProjectsFetchFailure = createAction('[Task] Projects Fetch Failure', props<{ error: string }>());

export const TaskProjectCreateRequest = createAction(
  '[Task] Project Create Request',
  props<{ payload: TaskProjectUpsertPayload; successMessage?: string }>(),
);
export const TaskProjectCreateSuccess = createAction(
  '[Task] Project Create Success',
  props<{ project: TaskProject; successMessage?: string }>(),
);
export const TaskProjectCreateFailure = createAction('[Task] Project Create Failure', props<{ error: string }>());

export const TaskProjectUpdateRequest = createAction(
  '[Task] Project Update Request',
  props<{ projectId: string; payload: TaskProjectUpsertPayload; successMessage?: string; silentSuccess?: boolean }>(),
);
export const TaskProjectUpdateSuccess = createAction(
  '[Task] Project Update Success',
  props<{ project: TaskProject; successMessage?: string; silentSuccess?: boolean }>(),
);
export const TaskProjectUpdateFailure = createAction('[Task] Project Update Failure', props<{ error: string }>());

export const TaskSprintsFetchRequest = createAction('[Task] Sprints Fetch Request', props<{ projectId: string }>());
export const TaskSprintsFetchSuccess = createAction('[Task] Sprints Fetch Success', props<{ sprints: TaskSprint[] }>());
export const TaskSprintsFetchFailure = createAction('[Task] Sprints Fetch Failure', props<{ error: string }>());

export const TaskSprintCreateRequest = createAction(
  '[Task] Sprint Create Request',
  props<{ projectId: string; payload: TaskSprintUpsertPayload; successMessage?: string }>(),
);
export const TaskSprintCreateSuccess = createAction(
  '[Task] Sprint Create Success',
  props<{ sprint: TaskSprint; successMessage?: string }>(),
);
export const TaskSprintCreateFailure = createAction('[Task] Sprint Create Failure', props<{ error: string }>());

export const TaskSprintUpdateRequest = createAction(
  '[Task] Sprint Update Request',
  props<{ projectId: string; sprintId: string; payload: TaskSprintUpsertPayload; successMessage?: string; silentSuccess?: boolean }>(),
);
export const TaskSprintUpdateSuccess = createAction(
  '[Task] Sprint Update Success',
  props<{ sprint: TaskSprint; successMessage?: string; silentSuccess?: boolean }>(),
);
export const TaskSprintUpdateFailure = createAction('[Task] Sprint Update Failure', props<{ error: string }>());

export const TaskItemsFetchRequest = createAction('[Task] Items Fetch Request', props<{ projectId?: string }>());
export const TaskItemsFetchSuccess = createAction('[Task] Items Fetch Success', props<{ tasks: TaskItem[] }>());
export const TaskItemsFetchFailure = createAction('[Task] Items Fetch Failure', props<{ error: string }>());

export const TaskDetailFetchRequest = createAction('[Task] Detail Fetch Request', props<{ taskId: string }>());
export const TaskDetailFetchSuccess = createAction('[Task] Detail Fetch Success', props<{ task: TaskItem }>());
export const TaskDetailFetchFailure = createAction('[Task] Detail Fetch Failure', props<{ error: string }>());

export const TaskCreateRequest = createAction(
  '[Task] Create Request',
  props<{ payload: TaskUpsertPayload; successMessage?: string }>(),
);
export const TaskCreateSuccess = createAction(
  '[Task] Create Success',
  props<{ task: TaskItem; successMessage?: string }>(),
);
export const TaskCreateFailure = createAction('[Task] Create Failure', props<{ error: string }>());

export const TaskUpdateRequest = createAction(
  '[Task] Update Request',
  props<{ taskId: string; payload: TaskUpsertPayload; successMessage?: string; silentSuccess?: boolean }>(),
);
export const TaskUpdateSuccess = createAction(
  '[Task] Update Success',
  props<{ task: TaskItem; successMessage?: string; silentSuccess?: boolean }>(),
);
export const TaskUpdateFailure = createAction('[Task] Update Failure', props<{ error: string }>());

export const TaskCommentCreateRequest = createAction('[Task] Comment Create Request', props<{ taskId: string; body: string }>());
export const TaskCommentCreateSuccess = createAction('[Task] Comment Create Success', props<{ task: TaskItem }>());
export const TaskCommentCreateFailure = createAction('[Task] Comment Create Failure', props<{ error: string }>());

export const TaskSubtaskCreateRequest = createAction(
  '[Task] Subtask Create Request',
  props<{ taskId: string; title: string; assignee_user_id?: string }>(),
);
export const TaskSubtaskCreateSuccess = createAction('[Task] Subtask Create Success', props<{ task: TaskItem }>());
export const TaskSubtaskCreateFailure = createAction('[Task] Subtask Create Failure', props<{ error: string }>());

export const TaskSubtaskUpdateRequest = createAction(
  '[Task] Subtask Update Request',
  props<{ taskId: string; subtaskId: string; title?: string; is_completed?: boolean; assignee_user_id?: string }>(),
);
export const TaskSubtaskUpdateSuccess = createAction('[Task] Subtask Update Success', props<{ task: TaskItem }>());
export const TaskSubtaskUpdateFailure = createAction('[Task] Subtask Update Failure', props<{ error: string }>());

export const TaskSubtaskDeleteRequest = createAction('[Task] Subtask Delete Request', props<{ taskId: string; subtaskId: string }>());
export const TaskSubtaskDeleteSuccess = createAction('[Task] Subtask Delete Success', props<{ task: TaskItem }>());
export const TaskSubtaskDeleteFailure = createAction('[Task] Subtask Delete Failure', props<{ error: string }>());

export const TaskAttachmentUploadRequest = createAction('[Task] Attachment Upload Request', props<{ taskId: string; file: File }>());
export const TaskAttachmentUploadSuccess = createAction(
  '[Task] Attachment Upload Success',
  props<{ response: TaskAttachmentUploadResponse }>(),
);
export const TaskAttachmentUploadFailure = createAction('[Task] Attachment Upload Failure', props<{ error: string }>());

export const TaskTimeEntryCreateRequest = createAction(
  '[Task] Time Entry Create Request',
  props<{ taskId: string; payload: TaskTimeEntryCreatePayload }>(),
);
export const TaskTimeEntryCreateSuccess = createAction('[Task] Time Entry Create Success', props<{ task: TaskItem }>());
export const TaskTimeEntryCreateFailure = createAction('[Task] Time Entry Create Failure', props<{ error: string }>());

export const TaskTimeEntryUpdateRequest = createAction(
  '[Task] Time Entry Update Request',
  props<{ taskId: string; timeEntryId: string; payload: TaskTimeEntryCreatePayload }>(),
);
export const TaskTimeEntryUpdateSuccess = createAction('[Task] Time Entry Update Success', props<{ task: TaskItem }>());
export const TaskTimeEntryUpdateFailure = createAction('[Task] Time Entry Update Failure', props<{ error: string }>());

export const TaskTimeEntryDeleteRequest = createAction(
  '[Task] Time Entry Delete Request',
  props<{ taskId: string; timeEntryId: string }>(),
);
export const TaskTimeEntryDeleteSuccess = createAction('[Task] Time Entry Delete Success', props<{ task: TaskItem }>());
export const TaskTimeEntryDeleteFailure = createAction('[Task] Time Entry Delete Failure', props<{ error: string }>());

export const TaskTimerStartRequest = createAction(
  '[Task] Timer Start Request',
  props<{ taskId: string; payload?: TaskTimerStartPayload }>(),
);
export const TaskTimerStartSuccess = createAction('[Task] Timer Start Success', props<{ task: TaskItem }>());
export const TaskTimerStartFailure = createAction('[Task] Timer Start Failure', props<{ error: string }>());

export const TaskTimerStopRequest = createAction(
  '[Task] Timer Stop Request',
  props<{ taskId: string; payload?: TaskTimerStopPayload }>(),
);
export const TaskTimerStopSuccess = createAction('[Task] Timer Stop Success', props<{ task: TaskItem }>());
export const TaskTimerStopFailure = createAction('[Task] Timer Stop Failure', props<{ error: string }>());

export const TaskNotificationsFetchRequest = createAction('[Task] Notifications Fetch Request');
export const TaskNotificationsFetchSuccess = createAction('[Task] Notifications Fetch Success', props<{ notifications: TaskNotification[] }>());
export const TaskNotificationsFetchFailure = createAction('[Task] Notifications Fetch Failure', props<{ error: string }>());

export const TaskNotificationReceived = createAction('[Task] Notification Received', props<{ notification: TaskNotification }>());
export const TaskExternalChangeReceived = createAction('[Task] External Change Received', props<{ projectId: string; taskId: string }>());
export const TaskNotificationMarkReadRequest = createAction('[Task] Notification Mark Read Request', props<{ notificationId: string }>());
export const TaskNotificationMarkReadSuccess = createAction('[Task] Notification Mark Read Success', props<{ notification: TaskNotification }>());
export const TaskNotificationMarkReadFailure = createAction('[Task] Notification Mark Read Failure', props<{ error: string }>());

export const TaskNotificationsMarkAllReadRequest = createAction('[Task] Notifications Mark All Read Request');
export const TaskNotificationsMarkAllReadSuccess = createAction('[Task] Notifications Mark All Read Success');
export const TaskNotificationsMarkAllReadFailure = createAction('[Task] Notifications Mark All Read Failure', props<{ error: string }>());

export const TaskNotificationsClearRequest = createAction('[Task] Notifications Clear Request');
export const TaskNotificationsClearSuccess = createAction('[Task] Notifications Clear Success');
export const TaskNotificationsClearFailure = createAction('[Task] Notifications Clear Failure', props<{ error: string }>());
